import React, { useState, useEffect } from "react";

import "react-toastify/dist/ReactToastify.css";
import { QueryClient, QueryClientProvider } from "react-query";

import { ptBR } from "@material-ui/core/locale";
import { createTheme, ThemeProvider } from "@material-ui/core/styles";
import { useMediaQuery } from "@material-ui/core";
import ColorModeContext from "./layout/themeContext";

import Routes from "./routes";
import PullToRefresh from "./components/PullToRefresh";

const queryClient = new QueryClient();

const THEME_PREFERENCES = ["light", "dark", "system"];

const readThemePreference = () => {
    try {
        const saved = window.localStorage.getItem("themePreference");
        if (THEME_PREFERENCES.includes(saved)) return saved;
        const legacy = window.localStorage.getItem("preferredTheme");
        if (legacy === "light" || legacy === "dark") return legacy;
    } catch (err) {
        // sem localStorage
    }
    return "system";
};

const App = () => {
    const [locale, setLocale] = useState();

    // noSsr: lê o tema do sistema já no primeiro render (evita piscar claro).
    const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)", { noSsr: true });

    // Preferência do menu da conta: "light", "dark" ou "system".
    // Quem já tinha escolhido um tema (chave antiga "preferredTheme") mantém
    // a escolha; quem nunca escolheu segue o sistema.
    const [themePreference, setThemePreferenceState] = useState(readThemePreference);
    const mode =
        themePreference === "system"
            ? prefersDarkMode ? "dark" : "light"
            : themePreference;

    const colorMode = React.useMemo(
        () => ({
            themePreference,
            setThemePreference: (value) => {
                if (!THEME_PREFERENCES.includes(value)) return;
                setThemePreferenceState(value);
                try {
                    window.localStorage.setItem("themePreference", value);
                } catch (err) {
                    // sem localStorage: vale só nesta sessão
                }
            },
            toggleColorMode: () => {
                const next = mode === "light" ? "dark" : "light";
                setThemePreferenceState(next);
                try {
                    window.localStorage.setItem("themePreference", next);
                } catch (err) {
                    // sem localStorage: vale só nesta sessão
                }
            },
        }),
        [themePreference, mode]
    );

    const theme = createTheme(
        {
            scrollbarStyles: {
                // Firefox
                scrollbarWidth: "thin",
                scrollbarColor: `${mode === "light" ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.18)"} transparent`,
                // Chromium / WebKit
                "&::-webkit-scrollbar": {
                    width: "6px",
                    height: "6px",
                },
                "&::-webkit-scrollbar-track": {
                    backgroundColor: "transparent",
                },
                "&::-webkit-scrollbar-thumb": {
                    borderRadius: "8px",
                    backgroundColor: mode === "light" ? "rgba(0,0,0,0.18)" : "rgba(255,255,255,0.18)",
                    transition: "background-color 0.2s",
                },
                "&::-webkit-scrollbar-thumb:hover": {
                    backgroundColor: mode === "light" ? "rgba(0,0,0,0.32)" : "rgba(255,255,255,0.32)",
                },
            },
            scrollbarStylesSoft: {
                "&::-webkit-scrollbar": {
                    width: "8px",
                },
                "&::-webkit-scrollbar-thumb": {
                    backgroundColor: mode === "light" ? "#F3F3F3" : "#333333",
                },
            },
            palette: {
                type: mode,
                primary: { main: "#2D7BE8" },
                background: {
                    default: mode === "light" ? "#F6F4EF" : "#1A2035",
                    paper:   mode === "light" ? "#ffffff" : "#1F2940",
                },
                textPrimary: mode === "light" ? "#2D7BE8" : "#FFFFFF",
                borderPrimary: mode === "light" ? "#2D7BE8" : "#FFFFFF",
                dark: { main: mode === "light" ? "#333333" : "#F3F3F3" },
                light: { main: mode === "light" ? "#F3F3F3" : "#333333" },
                tabHeaderBackground: mode === "light" ? "#EEE" : "#1F2940",
                optionsBackground: mode === "light" ? "#fafafa" : "#1F2940",
				options: mode === "light" ? "#fafafa" : "#243040",
				fontecor: mode === "light" ? "#2D7BE8" : "#fff",
                fancyBackground: mode === "light" ? "#F6F4EF" : "#1A2035",
				bordabox: mode === "light" ? "#f0f2f5" : "#2f3646",
				newmessagebox: mode === "light" ? "#f0f2f5" : "#2f3646",
				inputdigita: mode === "light" ? "#ffffff" : "#2f3646",
				contactdrawer: mode === "light" ? "#fff" : "#1F2940",
				announcements: mode === "light" ? "#ededed" : "#1F2940",
				login: mode === "light" ? "#fff" : "#151D2B",
				announcementspopover: mode === "light" ? "#fff" : "#1F2940",
				chatlist: mode === "light" ? "#eee" : "#1F2940",
				boxlist: mode === "light" ? "#ededed" : "#243040",
				boxchatlist: mode === "light" ? "#ededed" : "#243040",
                total: mode === "light" ? "#fff" : "#1F2940",
                messageIcons: mode === "light" ? "#54656f" : "#aebac1",
                inputBackground: mode === "light" ? "#FFFFFF" : "#243040",
                barraSuperior: mode === "light" ? "#2D7BE8" : "#1C2333",
				boxticket: mode === "light" ? "#EEE" : "#1F2940",
				campaigntab: mode === "light" ? "#ededed" : "#1F2940",
            },
            mode,
            typography: {
                fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
                fontSize: 14,
                body1: { fontSize: "0.875rem" },
                body2: { fontSize: "0.8125rem" },
                subtitle1: { fontSize: "0.875rem" },
                subtitle2: { fontSize: "0.8125rem" },
                caption: { fontSize: "0.75rem" },
            },
        },
        locale
    );

    useEffect(() => {
        const i18nlocale = localStorage.getItem("i18nextLng");
        const browserLocale =
            i18nlocale.substring(0, 2) + i18nlocale.substring(3, 5);

        if (browserLocale === "ptBR") {
            setLocale(ptBR);
        }
    }, []);

    useEffect(() => {
        document.body.dataset.theme = mode;
    }, [mode]);



    return (
        <ColorModeContext.Provider value={{ colorMode }}>
            <ThemeProvider theme={theme}>
                <QueryClientProvider client={queryClient}>
                    <PullToRefresh />
                    <Routes />
                </QueryClientProvider>
            </ThemeProvider>
        </ColorModeContext.Provider>
    );
};

export default App;
